import './util/dayjsSetup'
import React from "react"
import { Provider, useDispatch, useSelector } from "react-redux"
import { Router, Route, Switch } from "react-router-dom"
import { QueryClientProvider } from "@tanstack/react-query"
// import { ReactQueryDevtools } from "@tanstack/react-query-devtools" // Disabled - not in use yet

import "assets/vendors/style"
import "styles/wieldy.less"
import configureStore, { history } from "./appRedux/store"

import App from "./containers/App/index"
import { ResponsiveProvider } from "components/responsive"
import { updateWindowWidth } from "./appRedux/actions/setting-actions"
import { LoadingProvider } from "./contexts/LoadingContext"
import GlobalLoader from "./components/common/GlobalLoader"
import { queryClient } from "./services/api/queryClient"
import { ModalProvider } from "./services/modal/modalManager"
import ErrorBoundary from "./components/common/ErrorBoundary"
import MaintenancePage from "./components/MaintenancePage"
import GlobalApiErrorBanner from "./components/GlobalApiErrorBanner"
// import RouteLoadingInterceptor from "./components/common/RouteLoadingInterceptor" // Disabled - use manual loading only

/** True when the build was compiled with REACT_APP_MAINTENANCE_MODE=true */
const MAINTENANCE_MODE = process.env.REACT_APP_MAINTENANCE_MODE === "true"

const store = configureStore()
export { store }

const LayoutBootstrapper = () => {
  const dispatch = useDispatch()
  const authUser = useSelector(state => state?.auth?.authUser)

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined

    const syncViewport = () => {
      dispatch(updateWindowWidth(window.innerWidth))
    }

    syncViewport()

    window.addEventListener("resize", syncViewport)
    window.addEventListener("orientationchange", syncViewport)

    return () => {
      window.removeEventListener("resize", syncViewport)
      window.removeEventListener("orientationchange", syncViewport)
    }
  }, [dispatch])

  React.useEffect(() => {
    if (!authUser || typeof window === "undefined") return undefined

    const raf = requestAnimationFrame(() => {
      dispatch(updateWindowWidth(window.innerWidth))
      window.dispatchEvent(new Event("resize"))
    })

    return () => cancelAnimationFrame(raf)
  }, [authUser, dispatch])

  return null
}

const NextApp = () => {
  // Maintenance mode: bypass entire app tree — no Redux, no Router needed
  if (MAINTENANCE_MODE) {
    return <MaintenancePage type="deploying" />
  }

  return (
    <ErrorBoundary>
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <ModalProvider>
            <ResponsiveProvider>
              <LoadingProvider>
                <>
                  {/* Global API error banner — shows when server is unreachable */}
                  <GlobalApiErrorBanner />

                  <LayoutBootstrapper />
                  <GlobalLoader />
                  <Router history={history}>
                    <Switch>
                      <Route path="/" component={App} />
                    </Switch>
                  </Router>
                  {/* React Query Devtools - Disabled (not in use yet) */}
                  {/* {process.env.NODE_ENV === 'development' && (
                    <ReactQueryDevtools initialIsOpen={false} />
                  )} */}
                </>
              </LoadingProvider>
            </ResponsiveProvider>
          </ModalProvider>
        </QueryClientProvider>
      </Provider>
    </ErrorBoundary>
  )
}


export default NextApp
